import { Link } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import TeacherShell, { TeacherPageHeader } from '../../components/layout/TeacherShell';
import { Card, Button } from '../../components/ui';
import { Section, EmptyState } from '../../components/layout/AppShell';
import { cn } from '../../lib/cn';

export default function TeacherDashboard({ teacher, activities, active_sessions, stats, standalone_lessons, courses }) {
  const { t } = useTranslation();
  return (
    <TeacherShell scrollTitle={t('nav.dashboard')}>
      <TeacherPageHeader
        title={`${t('teacherDashboard.welcome')} ${teacher.first_name || t('teacherDashboard.teacher')}`}
        subtitle={t('teacherDashboard.subtitle')}
      />

      {/* Quick Actions */}
      <div className="mb-8">
        <Card className="p-5 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('teacherDashboard.readyToTeach')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                <span className="shimmer-text">{t('teacherDashboard.readyToTeachDesc')}</span>
              </p>
            </div>
            <Link href="/teacher/activities/new">
              <Button variant="primary" className="glow-button">
                {t('teacherDashboard.startNow')}
                <svg className="w-4 h-4 ml-1.5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Button>
            </Link>
          </div>
        </Card>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        <StatCard
          label={t('teacherDashboard.totalActivities')}
          value={stats.total_activities}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          }
        />
        <StatCard
          label={t('teacherDashboard.activeSessions')}
          value={stats.active_sessions}
          highlight={stats.active_sessions > 0}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          }
        />
        <StatCard
          label={t('teacherDashboard.totalStudents')}
          value={stats.total_students}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          }
        />
      </div>

      {/* Active Sessions */}
      {active_sessions.length > 0 && (
        <Section
          title={t('teacherDashboard.activeSessions')}
          description={t('teacherDashboard.studentsWorkingNow')}
        >
          <div className="space-y-3">
            {active_sessions.map((session) => (
              <ActiveSessionCard key={session.id} session={session} />
            ))}
          </div>
        </Section>
      )}

      {/* Your Courses */}
      {courses && courses.length > 0 && (
        <Section
          title={t('teacherDashboard.yourCourses')}
          action={
            <Link href="/teacher/courses">
              <Button variant="ghost" size="sm">{t('common.viewAll')}</Button>
            </Link>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((course) => (
              <Link key={course.id} href={`/teacher/courses/${course.id}`} className="block">
                <Card className="p-5 hover:shadow-md transition-shadow h-full">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 dark:text-white">{course.title}</h3>
                    <StatusBadge status={course.status} />
                  </div>
                  {course.description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">{course.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mt-auto pt-3 border-t border-gray-100 dark:border-gray-700">
                    <span>{course.unit_count} {course.unit_count === 1 ? t('common.unit') : t('common.units')}</span>
                    <span>{course.student_count} {course.student_count === 1 ? t('common.student') : t('common.students')}</span>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* Standalone Lessons */}
      {standalone_lessons && standalone_lessons.length > 0 && (
        <Section title={t('teacherDashboard.standaloneLessons')} description={t('teacherDashboard.standaloneLessonsDesc')}>
          <div className="space-y-3">
            {standalone_lessons.map((lesson) => (
              <StandaloneLessonCard key={lesson.id} lesson={lesson} />
            ))}
          </div>
        </Section>
      )}

      {/* Recent Activities */}
      <Section
        title={t('teacherDashboard.yourActivities')}
        action={
          <Link href="/teacher/activities/new">
            <Button variant="primary" size="sm">
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('teacherDashboard.newActivity')}
            </Button>
          </Link>
        }
      >
        {activities.length === 0 ? (
          <EmptyState
            title={t('teacherDashboard.noActivities')}
            description={t('teacherDashboard.noActivitiesHelp')}
            action={
              <Link href="/teacher/activities/new">
                <Button variant="secondary">{t('teacherDashboard.createFirstActivity')}</Button>
              </Link>
            }
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activities.map((activity) => (
              <ActivityCard key={activity.id} activity={activity} />
            ))}
          </div>
        )}
      </Section>
    </TeacherShell>
  );
}

function StatCard({ label, value, icon, highlight }) {
  return (
    <Card className={cn(
      'p-5',
      highlight && 'ring-2 ring-green-500 ring-offset-2 dark:ring-offset-gray-900'
    )}>
      <div className="flex items-center gap-4">
        <div className={cn(
          'p-3 rounded-lg',
          highlight ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
        )}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        </div>
      </div>
    </Card>
  );
}

function ActiveSessionCard({ session }) {
  const { t } = useTranslation();
  return (
    <Card className="p-4 bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{session.activity_title}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {session.student_count} {t('teacherDashboard.studentsJoined')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('teacherDashboard.joinCode')}</p>
            <p className="font-mono font-bold text-lg text-gray-900 dark:text-white">{session.join_code}</p>
          </div>
          <Link href={`/teacher/sessions/${session.id}`}>
            <Button variant="secondary" size="sm">{t('common.view')}</Button>
          </Link>
        </div>
      </div>
    </Card>
  );
}

function ActivityCard({ activity }) {
  const { t } = useTranslation();
  const statusColors = {
    draft: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    ready: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    archived: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
  };

  return (
    <Link href={`/teacher/activities/${activity.id}/edit`}>
      <Card className="p-5 hover:shadow-md transition-shadow cursor-pointer h-full">
        <div className="flex items-start justify-between mb-3">
          <span className={cn(
            'text-xs font-medium px-2 py-0.5 rounded',
            statusColors[activity.status] || statusColors.draft
          )}>
            {activity.status || 'draft'}
          </span>
          {activity.stimulus_type && (
            <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
              {activity.stimulus_type}
            </span>
          )}
        </div>

        <h3 className="font-semibold text-gray-900 dark:text-white mb-1 line-clamp-2">
          {activity.title}
        </h3>

        {activity.description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
            {activity.description}
          </p>
        )}

        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mt-auto pt-3 border-t border-gray-100 dark:border-gray-700">
          <span>
            {activity.approved_count}/{activity.question_count} {t('common.questions')}
          </span>
          {activity.session_count > 0 && (
            <span>{activity.session_count} {activity.session_count !== 1 ? t('common.sessions') : t('common.session')}</span>
          )}
        </div>
      </Card>
    </Link>
  );
}

function StatusBadge({ status }) {
  const colors = {
    draft: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    archived: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
    planned: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    ready: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    completed: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
  };
  return (
    <span className={cn('text-xs font-medium px-2 py-0.5 rounded', colors[status] || colors.draft)}>
      {status}
    </span>
  );
}

function StandaloneLessonCard({ lesson }) {
  const { t } = useTranslation();
  return (
    <Link href={`/teacher/standalone_lessons/${lesson.id}`} className="block">
      <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">{lesson.title}</h4>
            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>{lesson.activity_count} {lesson.activity_count === 1 ? t('common.activity') : t('common.activities')}</span>
              <span className="capitalize">{lesson.lesson_type.replace('_', ' ')}</span>
            </div>
          </div>
          <StatusBadge status={lesson.status} />
        </div>
      </Card>
    </Link>
  );
}
